import React, { useImperativeHandle } from "react";
import {
  Button,
  AlertDialog,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
  useDisclosure,
  useToast,
  Text,
} from "@chakra-ui/react";
import { getPlatform } from "@/lib/iap";

export interface DeleteAccountButtonProps {
  onAuth: () => void;
  onDelete: () => void;
  userStripeCustomerId?: string;
}

export interface DeleteAccountButtonHandle {
  close: () => void;
}

const PLATFORM = getPlatform();

const DeleteAccountButton = React.forwardRef<
  DeleteAccountButtonHandle,
  DeleteAccountButtonProps
>(({ onAuth, onDelete, userStripeCustomerId }, ref) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSubscriptionWarningOpen,
    onOpen: onSubscriptionWarningOpen,
    onClose: onSubscriptionWarningClose,
  } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement | null>(null);
  const subscriptionCancelRef = React.useRef<HTMLButtonElement | null>(null);
  const toast = useToast();

  useImperativeHandle(ref, () => ({
    close: () => {
      onClose();
      onSubscriptionWarningClose();
    },
  }));

  const handleDeleteAttempt = async () => {
    try {
      onAuth();
      onOpen();
    } catch (err) {
      toast({
        title: "Authentication Issue",
        description: `${err}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
        onCloseComplete: () => {
          onClose();
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    onClose();
    onSubscriptionWarningOpen();
  };

  const handleFinalDelete = () => {
    onSubscriptionWarningClose();
    onDelete();
  };

  return (
    <>
      <Button
        background={"var(--color-red)"}
        textColor={"white"}
        width={"100%"}
        onClick={handleDeleteAttempt}
        height={"var(--button-height)"}
        fontSize="var(--base-font-size)"
        rounded={"xl"}
        _focus={{}}
        _hover={{}}
        _active={{}}
      >
        Delete Account
      </Button>

      {/* First Dialog - Confirmation */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
        size={"xs"}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Permanently delete account?</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            Are you sure you want to delete your account? This cannot be undone.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onClose}
              rounded={"xl"}
              background={"var(--color-grey)"}
              textColor={"white"}
              fontSize="var(--base-font-size)"
              height={"var(--button-height)"}
              _focus={{}}
              _hover={{}}
              _active={{}}
            >
              No
            </Button>
            <Button
              ml={3}
              onClick={handleConfirmDelete}
              rounded={"xl"}
              background={"var(--color-red)"}
              textColor={"white"}
              fontSize="var(--base-font-size)"
              height={"var(--button-height)"}
              _focus={{}}
              _hover={{}}
              _active={{}}
            >
              Yes
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Second Dialog - Subscription Warning */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={subscriptionCancelRef}
        onClose={onSubscriptionWarningClose}
        isOpen={isSubscriptionWarningOpen}
        isCentered
        size={"xs"}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Important: Subscription Notice!</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Text fontWeight="bold" mb={4}>
              This will not cancel your subscription!
            </Text>
            <Text mb={2}>
              {PLATFORM === "web" && userStripeCustomerId !== ""
                ? "Please be sure to cancel your subscription via the billing portal in settings before deleting your account."
                : "Please be sure to cancel your subscription via App Store settings before deleting your account."}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={subscriptionCancelRef}
              onClick={onSubscriptionWarningClose}
              rounded={"xl"}
              background={"var(--color-grey)"}
              textColor={"white"}
              fontSize="var(--base-font-size-small)"
              height={"var(--button-height)"}
              _focus={{}}
              _hover={{}}
              _active={{}}
            >
              Cancel
            </Button>
            <Button
              ml={3}
              onClick={handleFinalDelete}
              rounded={"xl"}
              background={"var(--color-red)"}
              textColor={"white"}
              fontSize="var(--base-font-size-small)"
              height={"var(--button-height)"}
              _focus={{}}
              _hover={{}}
              _active={{}}
            >
              Delete Account
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

export default DeleteAccountButton;
